import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import 'react-lazy-load-image-component/src/effects/blur.css';
import apiService from '../services/apiService';
import {
  TeamContainer,
  TeamHero,
  Container,
  PageTitle,
  PageSubtitle,
  TeamContent,
  TeamGrid,
  TeamCard,
  MemberImage,
  MemberContent,
  MemberName,
  MemberRole,
  MemberBio,
  MemberContact,
  ContactLink,
  JoinTeamSection,
  JoinTeamTitle,
  JoinTeamText,
  JoinTeamButton
} from '../styles/TeamStyles';

const Team = () => {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);


  useEffect(() => {
    const fetchContent = async () => {
      try {
        const team = await apiService.getTeamContent();
        setTeamMembers(team);
      } catch (error) {
        console.error('Error fetching team content:', error);
        // Set default content if API fails
        setTeamMembers([
          {
            name: 'John Doe',
            role: 'Chief Executive Officer',
            bio: 'John brings over 15 years of experience in agricultural management and has been instrumental in growing Cocoa-Pro Corporation into the leading company it is today.',
            image: '/team-john.jpg',
            email: 'john@cocoapro.com'
          },
          {
            name: 'Jane Smith',
            role: 'Head of Operations',
            bio: 'Jane oversees all operational aspects of our cocoa processing facilities and ensures we maintain the highest quality standards in everything we do.',
            image: '/team-jane.jpg',
            email: 'jane@cocoapro.com'
          },
          {
            name: 'Michael Johnson',
            role: 'Quality Control Manager',
            bio: 'Michael leads our quality assurance team and has implemented industry-leading standards that have earned us international recognition.',
            image: '/team-michael.jpg',
            email: 'michael@cocoapro.com'
          },
          {
            name: 'Sarah Williams',
            role: 'Agricultural Consultant',
            bio: 'Sarah works directly with local farmers to improve their cultivation practices and helps them achieve better yields and quality.',
            image: '/team-sarah.jpg',
            email: 'sarah@cocoapro.com'
          },
          {
            name: 'David Brown',
            role: 'Export Manager',
            bio: 'David manages our international relationships and ensures our products reach global markets while maintaining compliance with international standards.',
            image: '/team-david.jpg',
            email: 'david@cocoapro.com'
          },
          {
            name: 'Lisa Davis',
            role: 'Research & Development',
            bio: 'Lisa leads our R&D efforts, constantly working to improve our processing techniques and develop new products for our customers.',
            image: '/team-lisa.jpg',
            email: 'lisa@cocoapro.com'
          }
        ]);
      }
    };

    fetchContent();
  }, []);

  const handleJoinTeam = () => {
    navigate('/contact');
  };



  return (
    <TeamContainer>
      {/* Hero Section */}
      <TeamHero>
        <Container>
          <PageTitle>Our Team</PageTitle>
          <PageSubtitle>
            Meet the dedicated professionals who make Cocoa-Pro Corporation a leader in the industry
          </PageSubtitle>
        </Container>
      </TeamHero>

      {/* Team Content */}
      <TeamContent>
        <Container>
          <TeamGrid>
            {teamMembers.map((member, index) => (
              <TeamCard key={index}>
                <MemberImage>
                  {member.image ? (
                    <LazyLoadImage
                      src={member.image}
                      alt={member.name}
                      effect="blur"
                      width="100%"
                      height="100%"
                      style={{ objectFit: 'cover' }}
                      placeholder={
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#f5f5f5'
                        }}>
                          <AiOutlineLoading3Quarters 
                            style={{ 
                              animation: 'spin 1s linear infinite',
                              fontSize: '2rem',
                              color: '#2E8B57'
                            }} 
                          />
                        </div>
                      }
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentNode.innerHTML = '👤';
                      }}
                    />
                  ) : (
                    '👤'
                  )}
                </MemberImage>
                <MemberContent>
                  <MemberName>{member.name}</MemberName>
                  <MemberRole>{member.role}</MemberRole>
                  <MemberBio>{member.bio}</MemberBio>
                  <MemberContact>
                    {member.email && (
                      <ContactLink href={`mailto:${member.email}`} title="Email">
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </ContactLink>
                    )}
                    {member.linkedin && (
                      <ContactLink href={member.linkedin} title="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <svg fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </ContactLink>
                    )}
                  </MemberContact>
                </MemberContent>
              </TeamCard>
            ))}
          </TeamGrid>
        </Container>
      </TeamContent>

      {/* Join Team Section */}
      <JoinTeamSection>
        <Container>
          <JoinTeamTitle>Join Our Team</JoinTeamTitle>
          <JoinTeamText>
            We're always looking for talented individuals who share our passion for excellence 
            and commitment to sustainable agriculture. Join us in making a difference.
          </JoinTeamText>
          <JoinTeamButton onClick={handleJoinTeam}>
            Contact Us
          </JoinTeamButton>
        </Container>
      </JoinTeamSection>
    </TeamContainer>
  );
};

export default Team;
